import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// Auth Context
// ============================================================================

interface User {
    token: string;
    role: string;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    login: (token: string, role?: string) => void;
    logout: () => void;
    isSuperAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Check for existing token on mount
        const token = localStorage.getItem('saca_admin_token');
        const role = localStorage.getItem('saca_admin_role') || 'Admin';

        if (token) {
            setUser({ token, role });
        }
        setIsLoading(false);
    }, []);

    const login = (token: string, role = 'Admin') => {
        localStorage.setItem('saca_admin_token', token);
        localStorage.setItem('saca_admin_role', role);
        setUser({ token, role });
    };

    const logout = () => {
        localStorage.removeItem('saca_admin_token');
        localStorage.removeItem('saca_admin_role');
        setUser(null);
        navigate('/admin-login');
    };

    const isSuperAdmin = () => {
        return user?.role?.toLowerCase().includes('superadmin') || false;
    };

    return (
        <AuthContext.Provider value={{ user, isLoading, login, logout, isSuperAdmin }}>
            {children}
        </AuthContext.Provider>
    );
};

// ============================================================================
// Sidebar Context
// ============================================================================

interface SidebarContextType {
    isCollapsed: boolean;
    isMobileOpen: boolean;
    toggleCollapse: () => void;
    toggleMobile: () => void;
    closeMobile: () => void;
    toggleGroup: (groupId: string) => void;
    isGroupExpanded: (groupId: string) => boolean;
}

const SidebarContext = createContext<SidebarContextType | null>(null);

export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within SidebarProvider');
    }
    return context;
};

interface SidebarProviderProps {
    children: ReactNode;
}

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<string[]>(['organizer-management', 'operations', 'users']);

    const toggleCollapse = () => setIsCollapsed(!isCollapsed);
    const toggleMobile = () => setIsMobileOpen(!isMobileOpen);
    const closeMobile = () => setIsMobileOpen(false);

    const toggleGroup = (groupId: string) => {
        setExpandedGroups((prev) =>
            prev.includes(groupId)
                ? prev.filter((id) => id !== groupId)
                : [...prev, groupId]
        );
    };

    const isGroupExpanded = (groupId: string) => expandedGroups.includes(groupId);

    return (
        <SidebarContext.Provider
            value={{
                isCollapsed,
                isMobileOpen,
                toggleCollapse,
                toggleMobile,
                closeMobile,
                toggleGroup,
                isGroupExpanded,
            }}
        >
            {children}
        </SidebarContext.Provider>
    );
};

// ============================================================================
// Combined Providers
// ============================================================================

interface AdminProvidersProps {
    children: ReactNode;
}

export const AdminProviders: React.FC<AdminProvidersProps> = ({ children }) => {
    return (
        <AuthProvider>
            <SidebarProvider>{children}</SidebarProvider>
        </AuthProvider>
    );
};

export default AdminProviders;
